/**
 * Exporta dados estruturados para PDF programatico (clean & compacto).
 *
 *   - Capa: faixa accent fina no topo, titulo, subtitulo com data/filtros.
 *   - Resumo: cards de KPI em grid (label uppercase + valor grande).
 *   - Secoes: titulo com underline accent, tabela densa via autoTable.
 *   - Footer: linha fina accent + numeracao discreta.
 *
 * Cores seguem o TEMA atual do usuario (light/dark): fundo da pagina,
 * textos, cards, bordas e linhas alternadas vem do tema passado em
 * `opts.tema`. Sem tema, cai no tema claro (TEMA_PDF_PADRAO) — preserva o
 * comportamento de quem nao passa tema (ex.: agendador de exportacao).
 *
 * Usa jsPDF + jspdf-autotable.
 */

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { displayCell, slugifyFilename, timestampForFilename } from "./formatters";
import { marcaDaExportacao } from "./temaExport";
import { inferStatusVariant } from "../status";

function hexToRgb(hex) {
  const s = String(hex || "").replace("#", "").trim();
  const full =
    s.length === 3
      ? s
          .split("")
          .map((c) => c + c)
          .join("")
      : s;
  const n = parseInt(full, 16);
  if (Number.isNaN(n) || full.length !== 6) return [0, 0, 0];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

// Tema claro padrao — equivale as cores fixas anteriores.
const TEMA_PDF_PADRAO = {
  background: "#ffffff",
  textPrimary: "#16181e",
  textSecondary: "#686d76",
  textMuted: "#949aa0",
  border: "#e4e6ea",
  cardBg: "#f9fafc",
  rowAlt: "#fafbfd",
  accent: "#ec3223",
  inkOnAccent: "#ffffff",
};

// Tons semânticos default (tema claro) — pills/barras de status quando o
// tema não os fornece (ex.: agendamentos antigos sem `tones`).
const TONES_PDF_PADRAO = {
  info: { bg: "#EBF1FE", text: "#1E429F" },
  success: { bg: "#E3F5EA", text: "#166534" },
  warning: { bg: "#FEF3D9", text: "#854D0E" },
  danger: { bg: "#FDE6E4", text: "#991B1B" },
  neutral: { bg: "#EEF0F4", text: "#3D4554" },
};

function montarCores(tema = {}) {
  const t = { ...TEMA_PDF_PADRAO, ...tema };
  const tonesHex = { ...TONES_PDF_PADRAO, ...(tema.tones || {}) };
  const tones = {};
  for (const [tom, cores] of Object.entries(tonesHex)) {
    tones[tom] = { bg: hexToRgb(cores.bg), text: hexToRgb(cores.text) };
  }
  return {
    background: hexToRgb(t.background),
    textPrimary: hexToRgb(t.textPrimary),
    textSecondary: hexToRgb(t.textSecondary),
    textMuted: hexToRgb(t.textMuted),
    border: hexToRgb(t.border),
    cardBg: hexToRgb(t.cardBg),
    rowAlt: hexToRgb(t.rowAlt),
    accent: hexToRgb(t.accent),
    inkOnAccent: hexToRgb(t.inkOnAccent),
    tones,
  };
}

// Densidade de tabela (Configurações → Aparência) refletida no PDF.
// "cozy" preserva exatamente os valores anteriores (retrocompat: quem não
// passa densidade — ex.: agendamentos antigos — não muda de aparência).
const ESTILOS_POR_DENSIDADE = {
  compact: { fontSize: 7,   cellPadding: { top: 1.0, right: 2.0, bottom: 1.0, left: 2.0 } },
  cozy:    { fontSize: 7.5, cellPadding: { top: 1.4, right: 2.2, bottom: 1.4, left: 2.2 } },
  comfort: { fontSize: 8.2, cellPadding: { top: 2.4, right: 2.6, bottom: 2.4, left: 2.6 } },
};

function estilosDaDensidade(densidade) {
  return ESTILOS_POR_DENSIDADE[densidade] || ESTILOS_POR_DENSIDADE.cozy;
}

function pintarFundo(doc, cores) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.setFillColor(...cores.background);
  doc.rect(0, 0, w, h, "F");
}

const MARGIN_MM = 12;

function alignFor(type) {
  switch (type) {
    case "currency":
    case "number":
    case "days":
    case "percentage":
      return "right";
    case "status":
    case "date":
    case "datetime":
      return "center";
    default:
      return "left";
  }
}

function widthFor(type) {
  switch (type) {
    case "currency":
      return 24;
    case "number":
    case "days":
    case "percentage":
      return 18;
    case "date":
      return 20;
    case "datetime":
      return 26;
    case "status":
      return 24;
    default:
      return undefined;
  }
}

function desenharCapa(doc, { titulo, filtros = [] }, cores) {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Cabeçalho no estilo das exportações de imagem (mockup GP): título
  // grande, marca + timestamp abaixo e régua accent de largura total.
  let y = MARGIN_MM + 5;
  doc.setTextColor(...cores.textPrimary);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(19);
  doc.text(titulo || "Exportação PharuERP", MARGIN_MM, y);

  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...cores.textSecondary);
  doc.text(
    `${marcaDaExportacao()}  •  ${new Date().toLocaleString("pt-BR")}`,
    MARGIN_MM,
    y
  );

  // Filtros ativos (linha própria, menor) — só quando existem.
  if (filtros && filtros.length > 0) {
    y += 4.2;
    doc.setFontSize(7.5);
    doc.setTextColor(...cores.textMuted);
    const linhas = doc.splitTextToSize(
      `Filtros: ${filtros.join(" · ")}`,
      pageWidth - MARGIN_MM * 2
    );
    doc.text(linhas, MARGIN_MM, y);
    y += (linhas.length - 1) * 3.4;
  }

  // Régua accent de largura total (como na imagem exportada).
  y += 3;
  doc.setFillColor(...cores.accent);
  doc.rect(MARGIN_MM, y, pageWidth - MARGIN_MM * 2, 0.9, "F");

  return y + 6;
}

function desenharResumo(doc, resumo, startY, cores) {
  if (!resumo || resumo.length === 0) return startY;
  const pageWidth = doc.internal.pageSize.getWidth();
  const usable = pageWidth - MARGIN_MM * 2;

  // Grid de cards no estilo do app (label uppercase à esquerda, valor
  // grande à direita na mesma linha): max 4 por linha.
  const total = resumo.length;
  const perRow = Math.min(total, total <= 4 ? total : 4);
  const cardW = (usable - (perRow - 1) * 3.5) / perRow;
  const cardH = 12;

  let y = startY;

  for (let i = 0; i < total; i++) {
    const col = i % perRow;
    if (col === 0 && i > 0) y += cardH + 3.5;

    const x = MARGIN_MM + col * (cardW + 3.5);

    // Card: superfície do tema com borda sutil, cantos bem arredondados.
    doc.setFillColor(...cores.cardBg);
    doc.setDrawColor(...cores.border);
    doc.setLineWidth(0.2);
    doc.roundedRect(x, y, cardW, cardH, 2.2, 2.2, "FD");

    // Label uppercase pequeno, alinhado à esquerda no meio vertical.
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.3);
    doc.setTextColor(...cores.textMuted);
    const label = String(resumo[i].label || "").toUpperCase();
    const labelLinhas = doc.splitTextToSize(label, cardW - 22);
    doc.text(labelLinhas[0] || "", x + 3.4, y + 7.4);

    // Valor: grande, à direita (baseline compartilhada com o label).
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...cores.textPrimary);
    const valor = displayCell(resumo[i].value, resumo[i].type || "text");
    const valorAjustado = doc.splitTextToSize(valor, cardW - 6)[0] || "";
    doc.text(valorAjustado, x + cardW - 3.4, y + 7.8, { align: "right" });
  }

  y += cardH + 6;
  return y;
}

function desenharTituloSecao(doc, label, startY, cores) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(...cores.textPrimary);
  doc.text(label, MARGIN_MM, startY);

  // Underline accent fino abaixo do título
  const textWidth = doc.getTextWidth(label);
  doc.setDrawColor(...cores.accent);
  doc.setLineWidth(0.45);
  doc.line(MARGIN_MM, startY + 0.9, MARGIN_MM + textWidth, startY + 0.9);
  return startY;
}

function desenharSecao(doc, secao, startY, cores, densidade) {
  const { label, columns, rows } = secao;
  const pageWidth = doc.internal.pageSize.getWidth();
  const usable = pageWidth - MARGIN_MM * 2;

  if (!rows || rows.length === 0) {
    desenharTituloSecao(doc, label, startY + 3, cores);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(...cores.textMuted);
    doc.text("Sem dados nesta seção.", MARGIN_MM, startY + 8);
    return startY + 12;
  }

  desenharTituloSecao(doc, label, startY + 3, cores);

  // Subtítulo com contagem de linhas, à direita
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...cores.textMuted);
  const contagem = `${rows.length} registro${rows.length !== 1 ? "s" : ""}`;
  doc.text(contagem, MARGIN_MM + usable, startY + 3, { align: "right" });

  // Cabeçalho uppercase como no app; status vira PILL desenhada à mão.
  const head = [columns.map((c) => String(c.label || "").toUpperCase())];
  const body = rows.map((r) =>
    columns.map((c) => displayCell(r[c.key], c.type || "text"))
  );
  const statusColIdx = columns.findIndex((c) => (c.type || "") === "status");
  // Tom de estado por linha (barra à esquerda + pill). Mesma regra da UI:
  // variants legados são normalizados (today→warning, como getToneTokens)
  // e, sem statusVariant na row, o tom é inferido do texto do status.
  const ALIAS_TONS = { today: "warning" };
  const normalizarTom = (v) => (v ? ALIAS_TONS[v] || v : null);
  const variantes = rows.map((r, i) => {
    const explicito = normalizarTom(r?.statusVariant);
    if (explicito && cores.tones[explicito]) return explicito;
    if (statusColIdx >= 0) {
      const inferido = normalizarTom(inferStatusVariant(body[i][statusColIdx]));
      if (inferido && cores.tones[inferido]) return inferido;
    }
    return explicito;
  });
  const toneDe = (variante) =>
    (variante && cores.tones[variante]) || cores.tones.neutral;

  // Tabelas longas podem gerar paginas novas internamente — pintamos o
  // fundo do tema nessas paginas (a inicial ja foi pintada por quem chamou).
  const paginaInicial = doc.internal.getNumberOfPages();
  const estilos = estilosDaDensidade(densidade);

  autoTable(doc, {
    startY: startY + 5.5,
    margin: { left: MARGIN_MM, right: MARGIN_MM },
    head,
    body,
    theme: "plain",
    willDrawPage: () => {
      if (doc.internal.getNumberOfPages() > paginaInicial) {
        pintarFundo(doc, cores);
      }
    },
    styles: {
      fontSize: estilos.fontSize,
      textColor: cores.textPrimary,
      cellPadding: estilos.cellPadding,
      lineWidth: 0,
    },
    // Header como no app: superfície do tema + texto muted uppercase
    // (o accent fica na régua do cabeçalho da página, não na tabela).
    headStyles: {
      fillColor: cores.cardBg,
      textColor: cores.textSecondary,
      fontStyle: "bold",
      fontSize: Math.max(estilos.fontSize - 0.7, 5.8),
      halign: "left",
      cellPadding: {
        ...estilos.cellPadding,
        top: estilos.cellPadding.top + 0.7,
        bottom: estilos.cellPadding.bottom + 0.7,
      },
      lineWidth: 0,
    },
    // Só separadores horizontais entre linhas (como na tela).
    bodyStyles: {
      lineColor: cores.border,
      lineWidth: { bottom: 0.14 },
    },
    alternateRowStyles: { fillColor: cores.rowAlt },
    columnStyles: columns.reduce((acc, c, idx) => {
      const halign = alignFor(c.type);
      const cellWidth = widthFor(c.type);
      acc[idx] = { halign, ...(cellWidth ? { cellWidth } : {}) };
      return acc;
    }, {}),
    didParseCell: (data) => {
      // Pill de status: o texto sai do fluxo do autoTable (vira "") e é
      // desenhado à mão em didDrawCell, por cima da zebra.
      if (data.section === "body" && data.column.index === statusColIdx) {
        const cru = Array.isArray(data.cell.text)
          ? data.cell.text.join(" ")
          : String(data.cell.text || "");
        data.cell.__statusTexto = cru;
        data.cell.text = [""];
      }
    },
    didDrawCell: (data) => {
      if (data.section !== "body") return;
      const variante = variantes[data.row.index];
      const tone = toneDe(variante);

      // Barra de tom na borda esquerda da linha (assinatura do mockup).
      if (data.column.index === 0 && variante) {
        doc.setFillColor(...tone.text);
        doc.rect(data.cell.x, data.cell.y, 1.0, data.cell.height, "F");
      }

      if (data.column.index === statusColIdx) {
        const texto = String(data.cell.__statusTexto || "").trim();
        if (!texto) return;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(Math.max(estilos.fontSize - 1, 5.6));
        const textW = doc.getTextWidth(texto);
        const pillH = Math.min(data.cell.height - 2.2, 5);
        const pillW = Math.min(textW + 5.4, data.cell.width - 2);
        const px = data.cell.x + (data.cell.width - pillW) / 2;
        const py = data.cell.y + (data.cell.height - pillH) / 2;
        doc.setFillColor(...tone.bg);
        doc.roundedRect(px, py, pillW, pillH, pillH / 2, pillH / 2, "F");
        doc.setTextColor(...tone.text);
        doc.text(
          texto,
          data.cell.x + data.cell.width / 2,
          py + pillH / 2,
          { align: "center", baseline: "middle" }
        );
      }
    },
  });

  return doc.lastAutoTable.finalY + 5;
}

function desenharFooter(doc, cores) {
  const pageCount = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    // Linha fina accent acima do rodapé
    doc.setDrawColor(...cores.accent);
    doc.setLineWidth(0.3);
    doc.line(
      MARGIN_MM,
      pageHeight - 9,
      pageWidth - MARGIN_MM,
      pageHeight - 9
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...cores.textMuted);
    doc.text(marcaDaExportacao(), MARGIN_MM, pageHeight - 5);
    doc.text(
      `${i} / ${pageCount}`,
      pageWidth - MARGIN_MM,
      pageHeight - 5,
      { align: "right" }
    );
  }
}

/**
 * @param {Object} opts
 * @param {string} opts.titulo
 * @param {Array<{ label, value, type? }>} [opts.resumo]
 * @param {Array<{ id, label, columns, rows }>} opts.secoes
 * @param {string[]} [opts.filtros]
 * @param {("portrait"|"landscape")} [opts.orientation="landscape"]
 * @param {Object} [opts.tema]  cores do tema atual (light/dark) em hex.
 * @param {("compact"|"cozy"|"comfort")} [opts.densidade="cozy"]  densidade
 *   de tabela das Configurações — reflete padding/fonte do autoTable.
 */
export function gerarPDF(opts) {
  const {
    titulo = "Exportação PharuERP",
    resumo = [],
    secoes = [],
    filtros = [],
    orientation = "landscape",
    tema,
    densidade,
  } = opts;

  const cores = montarCores(tema);
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation });

  // Fundo da pagina 1 (e demais paginas, conforme criadas).
  pintarFundo(doc, cores);

  let y = desenharCapa(doc, { titulo, filtros }, cores);
  y = desenharResumo(doc, resumo, y, cores);

  for (const secao of secoes) {
    if (y > doc.internal.pageSize.getHeight() - 30) {
      doc.addPage();
      pintarFundo(doc, cores);
      y = MARGIN_MM + 3;
    }
    y = desenharSecao(doc, secao, y, cores, densidade);
  }

  desenharFooter(doc, cores);
  return doc;
}

export function salvarPDF(opts) {
  const doc = gerarPDF(opts);
  const filename = `${slugifyFilename(opts.titulo)}_${timestampForFilename()}.pdf`;
  doc.save(filename);
  return filename;
}

/**
 * Gera o PDF e retorna { blob, filename } — sem fazer download.
 * Usado pelo executor de agendamento para escrever direto na pasta do
 * usuário via FileSystemDirectoryHandle.
 */
export function gerarBlobPDF(opts) {
  const doc = gerarPDF(opts);
  const blob = doc.output("blob");
  const filename = `${slugifyFilename(opts.titulo)}_${timestampForFilename()}.pdf`;
  return { blob, filename };
}
